/* AI Bridal Manager — Public landing page (v2 redesign).

   Premium editorial landing organized in 10 sections + sticky nav +
   global Footer. Every section uses the existing design system tokens
   (`font-serif-heading`, `label-uppercase`, primary champagne gold,
   `#fbf8f3` warm-white background, neutral palette) so the visual
   language stays consistent with the in-app experience.

   Sections — keep this list in sync with the in-page anchors so the
   sticky nav scrolls predictably:
     1. Hero
     2. #piattaforma   — Everything in one platform (8 cards)
     3. #moduli        — 3 premium modules (Wedding / Makeup / Hair)
     4. #come-funziona — 4-step workflow timeline
     5. Trial management — gallery & PDF sharing
     6. Wedding Day Assistant — countdown, traffic, timeline
     7. AI Manager — dark premium section
     8. Benefits — 4 metric tiles
     9. #prezzi        — 4-plan pricing grid
    10. FAQ            — accordion
    11. Final CTA
*/
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import Footer from "@/components/Footer";
import BrandLogo from "@/components/BrandLogo";
import {
  Sparkles, ArrowRight, Menu, X, Check, PlayCircle, ShieldCheck,
  FileSignature, ClipboardList, Camera, Calendar, Users2, Wallet,
  Brain, Sparkle, Scissors, Heart, MapPin, Clock, Car, Image as ImageIcon,
  TrendingUp, Target, Award, Mail, ChevronDown,
  LayoutDashboard, Users, Brush, FileText, Settings as SettingsIcon,
} from "lucide-react";
import useSupportEmail from "@/lib/useSupportEmail";

/* ─── shared animation preset ─── */
const reveal = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

const stagger = (i = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: i * 0.06 },
});

/* ================================================================
   DASHBOARD MOCKUP — preserved from v1 (champagne crisp pixels at any
   zoom). Powers the Hero visual.
   =============================================================== */
function DashboardMockup() {
  return (
    <div className="relative" data-testid="hero-dashboard-mockup" aria-hidden>
      <div className="pointer-events-none absolute -inset-12 -z-10 rounded-[40px] bg-primary/10 blur-3xl" />
      <div className="rounded-[18px] bg-white shadow-[0_30px_80px_-30px_rgba(60,40,20,0.35)] ring-1 ring-black/5 overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-3 border-b border-neutral-100 bg-white">
          <BrandLogo size={22} />
          <span className="font-serif-heading text-[14px] tracking-tight">
            AI Bridal Manager<sup className="text-[8px] text-primary">™</sup>
          </span>
        </div>
        <div className="grid grid-cols-[160px_1fr]">
          <aside className="border-r border-neutral-100 py-3 text-[11px] bg-white">
            <SideItem icon={LayoutDashboard} active>Dashboard</SideItem>
            <SideItem icon={Users}>Clienti</SideItem>
            <SideItem icon={Calendar}>Calendario</SideItem>
            <SideItem icon={Brush}>Prove</SideItem>
            <SideItem icon={FileText}>Contratti</SideItem>
            <SideItem icon={ClipboardList}>Preventivi</SideItem>
            <SideItem icon={Wallet}>Pagamenti</SideItem>
            <SideItem icon={SettingsIcon}>Impostazioni</SideItem>
          </aside>
          <div className="p-4 bg-[#fbfaf7]">
            <h3 className="font-serif-heading text-[18px] leading-tight">Benvenuta, Giulia</h3>
            <p className="text-[10px] text-neutral-500 mt-0.5">Ecco cosa hai in programma oggi.</p>
            <div className="mt-3 grid grid-cols-3 gap-2">
              <Kpi label="Matrimoni" value="8" sub="Questa settimana" />
              <Kpi label="Prove" value="12" sub="In agenda" />
              <Kpi label="Contratti" value="5" sub="Da firmare" />
            </div>
            <div className="mt-3 bg-white rounded-md border border-neutral-100 p-3">
              <div className="text-[10px] font-medium text-neutral-700">Prossimi appuntamenti</div>
              <div className="mt-2 divide-y divide-neutral-100">
                <Appt time="09:30" title="Prova trucco" who="Martina Bianchi" />
                <Appt time="11:00" title="Prova acconciatura" who="Sara Conti" />
                <Appt time="14:30" title="Matrimonio" who="Villa Rosantica" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
function SideItem({ icon: Icon, active, children }) {
  return (
    <div className={`flex items-center gap-2 px-3 py-1.5 mx-1.5 rounded-md ${active ? "bg-primary/10 text-primary font-medium" : "text-neutral-600"}`}>
      <Icon className="w-3 h-3" strokeWidth={1.5} />
      <span>{children}</span>
    </div>
  );
}
function Kpi({ label, value, sub }) {
  return (
    <div className="bg-white border border-neutral-100 rounded-md p-2">
      <div className="text-[9px] text-neutral-500">{label}</div>
      <div className="font-serif-heading text-[20px] leading-none mt-1">{value}</div>
      <div className="text-[9px] text-neutral-400 mt-1">{sub}</div>
    </div>
  );
}
function Appt({ time, title, who }) {
  return (
    <div className="flex items-center gap-2 py-1.5 text-[10px]">
      <span className="w-9 text-neutral-500 tabular-nums">{time}</span>
      <span className="flex-1 text-neutral-800">{title}</span>
      <span className="text-neutral-500 truncate max-w-[80px]">{who}</span>
    </div>
  );
}

/* ================================================================
   SECTION — HERO
   =============================================================== */
function Hero() {
  return (
    <section
      id="hero"
      className="relative isolate pt-32 sm:pt-40 pb-24 lg:pb-40 overflow-hidden bg-[#fbf8f3]"
      data-testid="hero"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-12 lg:gap-20 items-center">
          <motion.div {...reveal}>
            <span className="label-uppercase text-primary tracking-[0.22em]">
              Per Bridal Hair Stylist e Makeup Artist
            </span>
            <h1 className="mt-6 font-serif-heading text-[40px] sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Il gestionale professionale per <span className="italic text-primary">Bridal Hair Stylist</span> e Makeup Artist
            </h1>
            <p className="mt-7 text-lg sm:text-xl text-neutral-700 font-light leading-relaxed max-w-xl">
              Preventivi, contratti, prove con foto, timeline e Wedding Day Assistant in un'unica piattaforma progettata per il mondo bridal.
            </p>

            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <Link to="/register" data-testid="hero-cta-primary">
                <Button size="lg" className="rounded-full px-9 h-14 text-base font-medium shadow-[0_1px_2px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_24px_-8px_rgba(200,169,126,0.55)] transition-all">
                  Inizia gratis <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
                </Button>
              </Link>
              <a href="#come-funziona" data-testid="hero-cta-secondary">
                <Button variant="outline" size="lg" className="rounded-full px-7 h-14 text-base font-medium border-neutral-300 hover:bg-white">
                  <PlayCircle className="w-4 h-4 mr-2" strokeWidth={1.5} /> Guarda la demo
                </Button>
              </a>
            </div>

            {/* Trust badges */}
            <ul className="mt-10 flex flex-wrap gap-x-6 gap-y-3 text-[13px] text-neutral-600">
              {[
                "Nessuna carta richiesta",
                "Setup in 5 minuti",
                "GDPR compliant",
                "Made in Italy",
              ].map((t) => (
                <li key={t} className="inline-flex items-center gap-1.5">
                  <Check className="w-3.5 h-3.5 text-primary" strokeWidth={2} />
                  {t}
                </li>
              ))}
            </ul>
          </motion.div>

          {/* MacBook-style mockup */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.0, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
            className="relative"
          >
            <div className="relative">
              {/* MacBook frame */}
              <div className="rounded-t-[18px] bg-neutral-900 p-[10px] pb-0 shadow-[0_50px_100px_-30px_rgba(60,40,20,0.5)]">
                <DashboardMockup />
              </div>
              {/* MacBook base */}
              <div className="h-3 rounded-b-[20px] bg-gradient-to-b from-neutral-200 to-neutral-300 mx-[-12px] relative">
                <div className="absolute left-1/2 top-0 -translate-x-1/2 w-20 h-1 rounded-b-md bg-neutral-400/60" />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ================================================================
   SECTION 1 — PLATFORM (8 feature cards)
   =============================================================== */
const PLATFORM_FEATURES = [
  { icon: ClipboardList, title: "Preventivi Personalizzati",     desc: "Brochure-quality, multilingua, con il tuo brand e firma digitale online." },
  { icon: FileSignature,  title: "Contratti con Firma Digitale", desc: "Firma OTP via email, audit IP/dispositivo, hash documento. Tutto a norma." },
  { icon: Camera,         title: "Prove Makeup & Hair con Foto", desc: "Schede tecniche, prodotti, allergie, fino a 6 foto comparative per prova." },
  { icon: Calendar,       title: "Timeline Automatica",          desc: "Orari, location, partenze e arrivi calcolati con traffico in tempo reale." },
  { icon: MapPin,         title: "Wedding Day Assistant",        desc: "Countdown, percorso, collaboratori e checklist sul tuo telefono." },
  { icon: Users2,         title: "Team e Collaboratori",         desc: "Assegna i tuoi operatori, condividi schede e foto, coordina ogni dettaglio." },
  { icon: Wallet,         title: "Pagamenti e Scadenze",         desc: "Acconti multipli, scadenzario automatico, alert prima del saldo." },
  { icon: Brain,          title: "AI Manager™",                  desc: "KPI, forecast, conversioni preventivi e suggerimenti strategici dai tuoi dati." },
];

function PlatformSection() {
  return (
    <section id="piattaforma" className="py-28 sm:py-36 bg-white" data-testid="section-platform">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <motion.div {...reveal} className="max-w-3xl">
          <span className="label-uppercase text-primary">Piattaforma</span>
          <h2 className="mt-4 font-serif-heading text-4xl sm:text-5xl tracking-tight">
            Tutto ciò che ti serve per gestire ogni sposa
          </h2>
          <p className="mt-5 text-lg text-neutral-600 font-light leading-relaxed">
            Un unico spazio di lavoro per il flusso completo, dal primo contatto al giorno del matrimonio.
          </p>
        </motion.div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {PLATFORM_FEATURES.map((f, i) => (
            <motion.div
              key={f.title}
              {...stagger(i)}
              className="group rounded-2xl border border-neutral-100 bg-white p-7 hover:shadow-[0_24px_48px_-24px_rgba(200,169,126,0.4)] hover:-translate-y-1 transition-all duration-300"
              data-testid={`platform-card-${i}`}
            >
              <span className="inline-flex w-11 h-11 rounded-xl bg-primary/10 text-primary items-center justify-center group-hover:bg-primary/20 transition-colors">
                <f.icon className="w-5 h-5" strokeWidth={1.5} />
              </span>
              <h3 className="mt-5 font-serif-heading text-xl leading-tight">{f.title}</h3>
              <p className="mt-2 text-sm text-neutral-600 leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ================================================================
   SECTION 2 — MODULES (Wedding / Makeup / Hair)
   =============================================================== */
const MODULES = [
  { icon: Heart,    title: "Wedding Management", desc: "Location, orari, timeline, logistica e coordinamento completo." },
  { icon: Sparkle,  title: "Bridal Makeup",      desc: "Scheda tecnica, prodotti, allergie, prove e galleria fotografica." },
  { icon: Scissors, title: "Bridal Hair",        desc: "Acconciature, accessori, extension, prove e foto comparative." },
];

function ModulesSection() {
  return (
    <section id="moduli" className="py-28 sm:py-36 bg-[#fbf8f3]" data-testid="section-modules">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <motion.div {...reveal} className="max-w-3xl">
          <span className="label-uppercase text-primary">Moduli</span>
          <h2 className="mt-4 font-serif-heading text-4xl sm:text-5xl tracking-tight">
            Moduli professionali integrati
          </h2>
        </motion.div>

        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {MODULES.map((m, i) => (
            <motion.div
              key={m.title}
              {...stagger(i)}
              className="relative overflow-hidden rounded-3xl bg-white border border-neutral-100 p-10 hover:shadow-[0_30px_60px_-30px_rgba(60,40,20,0.25)] hover:-translate-y-1 transition-all duration-300"
              data-testid={`module-card-${i}`}
            >
              <span className="inline-flex w-14 h-14 rounded-2xl bg-primary/10 text-primary items-center justify-center">
                <m.icon className="w-6 h-6" strokeWidth={1.5} />
              </span>
              <h3 className="mt-6 font-serif-heading text-2xl leading-tight">{m.title}</h3>
              <p className="mt-3 text-neutral-600 leading-relaxed">{m.desc}</p>
              <div aria-hidden className="pointer-events-none absolute -right-12 -bottom-12 w-44 h-44 rounded-full bg-primary/5 blur-2xl" />
            </motion.div>
          ))}
        </div>

        <motion.p {...reveal} className="mt-10 text-center text-sm text-neutral-500">
          Attiva <span className="text-neutral-800 font-medium">Makeup e Hair insieme</span> nei piani PRO e AI MANAGER™.
        </motion.p>
      </div>
    </section>
  );
}

/* ================================================================
   SECTION 3 — HOW IT WORKS (4-step timeline)
   =============================================================== */
const STEPS = [
  { n: "01", title: "La sposa compila il questionario", desc: "Form personalizzato con il tuo brand, integrato con WhatsApp e Instagram." },
  { n: "02", title: "Crei preventivo e contratto",       desc: "Brochure premium multilingua, firma digitale OTP, pagamenti tracciati." },
  { n: "03", title: "Gestisci prove e fotografie",       desc: "Schede tecniche, prove fissate sul calendario, foto condivise via link privato." },
  { n: "04", title: "Coordini il Wedding Day",            desc: "Timeline live, percorso con traffico, collaboratori e checklist nel palmo." },
];

function HowItWorksSection() {
  return (
    <section id="come-funziona" className="py-28 sm:py-36 bg-white" data-testid="section-how-it-works">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <motion.div {...reveal} className="max-w-3xl">
          <span className="label-uppercase text-primary">Come funziona</span>
          <h2 className="mt-4 font-serif-heading text-4xl sm:text-5xl tracking-tight">
            Dalla prima richiesta al giorno del matrimonio
          </h2>
        </motion.div>

        <ol className="mt-14 relative">
          {/* Vertical line on desktop, hidden on mobile */}
          <span aria-hidden className="hidden md:block absolute left-[34px] top-2 bottom-2 w-px bg-gradient-to-b from-primary/40 via-primary/20 to-transparent" />
          {STEPS.map((s, i) => (
            <motion.li key={s.n} {...stagger(i)} className="relative grid md:grid-cols-[80px_1fr] gap-4 md:gap-8 pb-12 last:pb-0">
              <div className="flex items-start">
                <span className="inline-flex w-[72px] h-[72px] rounded-2xl bg-white ring-1 ring-primary/30 shadow-[0_8px_24px_-12px_rgba(200,169,126,0.5)] items-center justify-center font-serif-heading text-2xl text-primary">
                  {s.n}
                </span>
              </div>
              <div className="md:pt-3">
                <h3 className="font-serif-heading text-2xl leading-tight">{s.title}</h3>
                <p className="mt-2 text-neutral-600 leading-relaxed max-w-2xl">{s.desc}</p>
              </div>
            </motion.li>
          ))}
        </ol>
      </div>
    </section>
  );
}

/* ================================================================
   SECTION 4 — TRIAL SESSION MANAGEMENT
   =============================================================== */
function TrialSection() {
  return (
    <section className="py-28 sm:py-36 bg-[#fbf8f3]" data-testid="section-trial">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 items-center">
        <motion.div {...reveal}>
          <span className="label-uppercase text-primary">Prove</span>
          <h2 className="mt-4 font-serif-heading text-4xl sm:text-5xl tracking-tight">
            Gestisci ogni prova in modo professionale
          </h2>
          <ul className="mt-8 space-y-4">
            {[
              { icon: ClipboardList, label: "Note dettagliate e scheda tecnica" },
              { icon: Sparkle,       label: "Prodotti utilizzati e allergie" },
              { icon: ImageIcon,     label: "Foto prima e dopo, fino a 6 per prova" },
              { icon: FileText,      label: "PDF brandizzato condivisibile" },
              { icon: ShieldCheck,   label: "Link privato per la sposa, sempre revocabile" },
            ].map((f) => (
              <li key={f.label} className="flex items-start gap-3.5">
                <span className="inline-flex w-9 h-9 rounded-lg bg-primary/10 text-primary items-center justify-center shrink-0">
                  <f.icon className="w-4 h-4" strokeWidth={1.5} />
                </span>
                <span className="pt-1.5 text-neutral-700 leading-snug">{f.label}</span>
              </li>
            ))}
          </ul>
        </motion.div>

        {/* Trial gallery mockup */}
        <motion.div
          {...reveal}
          className="relative rounded-3xl bg-white border border-neutral-100 p-6 shadow-[0_30px_60px_-30px_rgba(60,40,20,0.3)]"
          data-testid="trial-gallery-mockup"
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="font-serif-heading text-lg">Prova Makeup · Martina B.</div>
              <div className="text-xs text-neutral-500 mt-0.5">Sabato 22 marzo · 10:30 · Studio Roma</div>
            </div>
            <span className="text-[10px] tracking-[0.18em] uppercase text-primary bg-primary/10 px-2 py-1 rounded-sm">Completata</span>
          </div>
          <div className="mt-5 grid grid-cols-3 gap-2.5">
            {[
              "https://images.unsplash.com/photo-1522335789203-aaa370b3a3ed?w=300&q=70",
              "https://images.unsplash.com/photo-1457972729786-0411a3b2b626?w=300&q=70",
              "https://images.unsplash.com/photo-1503104834685-7205e8607eb9?w=300&q=70",
              "https://images.unsplash.com/photo-1500917293891-ef795e70e1f6?w=300&q=70",
              "https://images.unsplash.com/photo-1487412947147-5cebf100ffc2?w=300&q=70",
              "https://images.unsplash.com/photo-1519415943484-9fa1873496d4?w=300&q=70",
            ].map((src, i) => (
              <div key={i} className="aspect-square rounded-lg overflow-hidden bg-secondary">
                <img src={src} alt={`Prova ${i + 1}`} loading="lazy" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
          <div className="mt-5 flex items-center justify-between text-xs">
            <span className="text-neutral-500">6/6 foto · 4,2 MB</span>
            <button type="button" className="text-primary font-medium inline-flex items-center gap-1">
              Condividi PDF <ArrowRight className="w-3 h-3" strokeWidth={2} />
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ================================================================
   SECTION 5 — WEDDING DAY ASSISTANT
   =============================================================== */
function WeddingDaySection() {
  return (
    <section className="py-28 sm:py-36 bg-white" data-testid="section-wedding-day">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 grid lg:grid-cols-[1.1fr_1fr] gap-16 items-center">
        {/* Phone mockup left */}
        <motion.div
          {...reveal}
          className="order-2 lg:order-1 relative mx-auto max-w-[340px] w-full"
          data-testid="wedding-day-mockup"
        >
          <div className="rounded-[42px] bg-neutral-950 p-2 shadow-[0_40px_80px_-30px_rgba(60,40,20,0.55)]">
            <div className="rounded-[34px] bg-[#fbf8f3] overflow-hidden">
              <div className="h-7 flex items-center justify-center bg-neutral-950">
                <div className="w-20 h-1.5 rounded-full bg-neutral-700" />
              </div>
              <div className="p-5">
                <div className="text-[10px] tracking-[0.18em] uppercase text-primary">Wedding day</div>
                <div className="font-serif-heading text-2xl mt-1 leading-tight">Giulia & Marco</div>
                <div className="text-xs text-neutral-500">Villa Rosantica · 22 giugno 2026</div>

                {/* Countdown */}
                <div className="mt-5 rounded-xl bg-white border border-neutral-100 p-4 text-center">
                  <div className="text-[10px] tracking-wider uppercase text-neutral-500">Mancano</div>
                  <div className="font-serif-heading text-4xl mt-1 text-neutral-900 tabular-nums">2g 04h 18m</div>
                </div>

                <div className="mt-4 space-y-2.5">
                  <PhoneRow icon={Car} label="Partenza" value="06:30" sub="33 km · 47 min" />
                  <PhoneRow icon={Clock} label="Inizio servizio" value="07:15" sub="Suite della sposa" />
                  <PhoneRow icon={Users2} label="Team" value="3 op." sub="Sara · Elena · Luca" />
                </div>

                <button type="button" className="mt-5 w-full rounded-full bg-primary text-white text-xs font-medium py-2.5">
                  Apri timeline completa
                </button>
              </div>
            </div>
          </div>
          <div aria-hidden className="pointer-events-none absolute -inset-10 -z-10 rounded-[40px] bg-primary/15 blur-3xl" />
        </motion.div>

        <motion.div {...reveal} className="order-1 lg:order-2">
          <span className="label-uppercase text-primary">Wedding Day Assistant</span>
          <h2 className="mt-4 font-serif-heading text-4xl sm:text-5xl tracking-tight">
            Il tuo assistente personale per il giorno del matrimonio
          </h2>
          <ul className="mt-8 space-y-3.5">
            {[
              { icon: Clock,  label: "Countdown evento e checklist" },
              { icon: Car,    label: "Orario di partenza con traffico in tempo reale" },
              { icon: MapPin, label: "Percorso e logistica integrati" },
              { icon: Calendar, label: "Timeline completa orario per orario" },
              { icon: Users2, label: "Collaboratori assegnati e contattabili" },
            ].map((f) => (
              <li key={f.label} className="flex items-start gap-3.5">
                <span className="inline-flex w-9 h-9 rounded-lg bg-primary/10 text-primary items-center justify-center shrink-0">
                  <f.icon className="w-4 h-4" strokeWidth={1.5} />
                </span>
                <span className="pt-1.5 text-neutral-700 leading-snug">{f.label}</span>
              </li>
            ))}
          </ul>
        </motion.div>
      </div>
    </section>
  );
}

function PhoneRow({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-lg bg-white border border-neutral-100 p-2.5 flex items-center gap-2.5">
      <span className="inline-flex w-7 h-7 rounded-md bg-primary/10 text-primary items-center justify-center shrink-0">
        <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-neutral-500">{label}</div>
        <div className="text-[11px] text-neutral-500">{sub}</div>
      </div>
      <div className="text-xs font-medium text-neutral-900 tabular-nums">{value}</div>
    </div>
  );
}

/* ================================================================
   SECTION 6 — AI MANAGER (dark premium)
   =============================================================== */
function AIManagerSection() {
  return (
    <section className="relative py-28 sm:py-36 bg-neutral-950 text-white overflow-hidden" data-testid="section-ai-manager">
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute top-1/3 left-1/4 w-96 h-96 rounded-full bg-primary/15 blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full bg-primary/10 blur-[100px]" />
      </div>
      <div className="relative max-w-5xl mx-auto px-6 lg:px-10 text-center">
        <motion.span {...reveal} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/20 text-primary text-xs tracking-[0.2em] uppercase">
          <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} />
          AI Manager™
        </motion.span>
        <motion.h2 {...reveal} className="mt-6 font-serif-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight">
          Trasforma i dati in decisioni strategiche
        </motion.h2>
        <motion.p {...reveal} className="mt-6 text-lg text-neutral-300 font-light leading-relaxed max-w-2xl mx-auto">
          Un assistente esperto che analizza il tuo business e ti suggerisce dove investire tempo, dove alzare i prezzi e dove migliorare la conversione.
        </motion.p>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
          {[
            { icon: TrendingUp, label: "Conversione preventivi" },
            { icon: Target,     label: "Forecast di fatturato" },
            { icon: Wallet,     label: "Redditività per evento" },
            { icon: Award,      label: "KPI automatici" },
            { icon: Brain,      label: "Insight intelligenti" },
          ].map((f, i) => (
            <motion.div
              key={f.label}
              {...stagger(i)}
              className="rounded-2xl bg-white/5 border border-white/10 backdrop-blur p-5 text-left hover:bg-white/8 hover:border-white/20 transition-colors"
            >
              <span className="inline-flex w-10 h-10 rounded-lg bg-primary/20 text-primary items-center justify-center">
                <f.icon className="w-4 h-4" strokeWidth={1.5} />
              </span>
              <div className="mt-3 text-sm font-medium leading-snug">{f.label}</div>
            </motion.div>
          ))}
        </div>

        <motion.div {...reveal} className="mt-14">
          <Link to="/register" data-testid="ai-manager-cta">
            <Button size="lg" className="rounded-full px-8 h-13 bg-white text-neutral-950 hover:bg-neutral-100">
              Scopri AI MANAGER™ <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

/* ================================================================
   SECTION 7 — BENEFITS
   =============================================================== */
function BenefitsSection() {
  const METRICS = [
    { value: "-80%",  label: "Tempo amministrativo", sub: "Documenti, firme, follow-up automatici." },
    { value: "+40%",  label: "Conversione preventivi", sub: "Brochure premium e CTA chiari sui link pubblici." },
    { value: "0",     label: "Documenti dispersi",    sub: "Cronologia completa di ogni cliente, sempre." },
    { value: "100%",  label: "Workflow professionale", sub: "Dal primo contatto al saldo, ordinato." },
  ];
  return (
    <section className="py-28 sm:py-36 bg-[#fbf8f3]" data-testid="section-benefits">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <motion.div {...reveal} className="max-w-3xl">
          <span className="label-uppercase text-primary">Risultati</span>
          <h2 className="mt-4 font-serif-heading text-4xl sm:text-5xl tracking-tight">
            Più organizzazione. Più tempo. Più fatturato.
          </h2>
        </motion.div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {METRICS.map((m, i) => (
            <motion.div
              key={m.label}
              {...stagger(i)}
              className="rounded-3xl bg-white border border-neutral-100 p-8 hover:shadow-[0_24px_48px_-24px_rgba(200,169,126,0.4)] hover:-translate-y-1 transition-all duration-300"
            >
              <div className="font-serif-heading text-5xl text-primary leading-none tracking-tight">{m.value}</div>
              <div className="mt-4 font-medium text-neutral-900">{m.label}</div>
              <p className="mt-1.5 text-sm text-neutral-600 leading-snug">{m.sub}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ================================================================
   SECTION 8 — PRICING (4 plans)
   =============================================================== */
const PLANS = [
  {
    id: "free",
    name: "Free",
    price: "€0",
    period: "/sempre",
    features: ["1 cliente", "1 preventivo", "1 contratto base", "1 prova", "Fino a 3 foto"],
  },
  {
    id: "basic",
    name: "Basic",
    price: "€39",
    period: "+ IVA / mese",
    features: ["30 clienti/anno", "30 contratti/anno", "Makeup oppure Hair", "Preventivi personalizzati", "Firma digitale", "3 foto per prova"],
  },
  {
    id: "pro",
    name: "Pro",
    price: "€59",
    period: "+ IVA / mese",
    badge: "Più scelto",
    highlight: true,
    features: [
      "Clienti illimitati",
      "Contratti illimitati",
      "Makeup + Hair",
      "Timeline automatica",
      "Wedding Day Assistant",
      "6 foto per prova",
      "Team e collaboratori",
    ],
  },
  {
    id: "ai_manager",
    name: "AI Manager™",
    price: "€79",
    period: "+ IVA / mese",
    features: ["Tutto del PRO", "AI Manager™", "Analytics avanzati", "Forecast e KPI"],
  },
];

function PricingSection() {
  return (
    <section id="prezzi" className="py-28 sm:py-36 bg-white" data-testid="section-pricing">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div {...reveal} className="max-w-3xl">
          <span className="label-uppercase text-primary">Prezzi</span>
          <h2 className="mt-4 font-serif-heading text-4xl sm:text-5xl tracking-tight">
            Un piano per ogni momento della tua attività
          </h2>
          <p className="mt-5 text-lg text-neutral-600 font-light leading-relaxed">
            Inizia gratis, passa a Basic o Pro quando il tuo studio cresce. AI MANAGER™ quando vuoi trasformare i dati in decisioni.
          </p>
        </motion.div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {PLANS.map((p, i) => (
            <motion.div
              key={p.id}
              {...stagger(i)}
              className={`relative rounded-3xl p-8 border transition-all duration-300 ${
                p.highlight
                  ? "bg-neutral-950 text-white border-neutral-950 shadow-[0_30px_60px_-30px_rgba(60,40,20,0.45)] -translate-y-2"
                  : "bg-white text-neutral-900 border-neutral-100 hover:shadow-[0_24px_48px_-24px_rgba(200,169,126,0.4)] hover:-translate-y-1"
              }`}
              data-testid={`pricing-plan-${p.id}`}
            >
              {p.badge && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-white text-[10px] tracking-[0.2em] uppercase px-3 py-1 rounded-full shadow-sm">
                  {p.badge}
                </span>
              )}
              <div className="font-serif-heading text-2xl">{p.name}</div>
              <div className="mt-5 flex items-baseline gap-1">
                <span className="font-serif-heading text-5xl tracking-tight">{p.price}</span>
                <span className={`text-xs ${p.highlight ? "text-neutral-400" : "text-neutral-500"}`}>{p.period}</span>
              </div>
              <ul className={`mt-7 space-y-3 text-sm ${p.highlight ? "text-neutral-200" : "text-neutral-700"}`}>
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5">
                    <Check className={`w-4 h-4 mt-0.5 shrink-0 ${p.highlight ? "text-primary" : "text-primary"}`} strokeWidth={2} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Link to="/register" className="block mt-8" data-testid={`pricing-cta-${p.id}`}>
                <Button
                  size="lg"
                  className={`w-full rounded-full ${p.highlight ? "bg-primary hover:bg-primary/90 text-white" : "bg-neutral-900 hover:bg-neutral-800 text-white"}`}
                >
                  Inizia ora
                </Button>
              </Link>
            </motion.div>
          ))}
        </div>

        <p className="mt-10 text-center text-xs text-neutral-500">
          IVA esclusa · Pagamenti gestiti da Stripe · Puoi annullare in qualsiasi momento.
        </p>
      </div>
    </section>
  );
}

/* ================================================================
   SECTION 9 — FAQ (accordion)
   =============================================================== */
const FAQS = [
  { q: "Posso provarlo gratuitamente?", a: "Sì. Il piano Free è permanente e include 1 cliente, 1 preventivo, 1 contratto, 1 prova e fino a 3 foto. Nessuna carta richiesta." },
  { q: "È adatto a Hair Stylist e Makeup Artist?", a: "Sì. AI Bridal Manager™ è pensato specificamente per il mondo bridal: schede tecniche, prodotti, allergie, acconciature, extension e prove fotografiche sono progettati per il flusso reale di una sposa." },
  { q: "Posso inviare preventivi e contratti online?", a: "Sì. Preventivi e contratti generano automaticamente un link condivisibile con la sposa, brochure-quality, multilingua. La firma è digitale via OTP email, conforme alle normative." },
  { q: "I clienti possono vedere le foto delle prove?", a: "Sì. Ogni prova ha un link privato condivisibile con la sposa che mostra foto e PDF della scheda. Il link è revocabile in qualsiasi momento." },
  { q: "Posso usare Makeup e Hair insieme?", a: "Sì, con i piani PRO e AI MANAGER™. Il piano Basic include un modulo (Makeup oppure Hair); puoi sempre fare upgrade in qualsiasi momento." },
  { q: "Come funziona AI Manager™?", a: "AI Manager™ analizza i tuoi dati reali (clienti, preventivi, conversioni, eventi) e ti fornisce KPI automatici, forecast di fatturato e insight strategici per far crescere il tuo business." },
];

function FaqSection() {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="py-28 sm:py-36 bg-[#fbf8f3]" data-testid="section-faq">
      <div className="max-w-3xl mx-auto px-6 lg:px-10">
        <motion.div {...reveal}>
          <span className="label-uppercase text-primary">FAQ</span>
          <h2 className="mt-4 font-serif-heading text-4xl sm:text-5xl tracking-tight">
            Domande frequenti
          </h2>
        </motion.div>

        <div className="mt-12 space-y-3">
          {FAQS.map((f, i) => (
            <motion.div
              key={f.q}
              {...stagger(i)}
              className="rounded-2xl bg-white border border-neutral-100 overflow-hidden"
              data-testid={`faq-${i}`}
            >
              <button
                type="button"
                onClick={() => setOpen(open === i ? -1 : i)}
                className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left hover:bg-neutral-50/50 transition-colors"
                data-testid={`faq-toggle-${i}`}
              >
                <span className="font-serif-heading text-lg leading-snug">{f.q}</span>
                <ChevronDown
                  className={`w-5 h-5 text-primary shrink-0 transition-transform duration-300 ${open === i ? "rotate-180" : ""}`}
                  strokeWidth={1.5}
                />
              </button>
              <div
                className={`grid transition-all duration-300 ${open === i ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}
              >
                <div className="overflow-hidden">
                  <p className="px-6 pb-5 text-neutral-600 leading-relaxed">{f.a}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ================================================================
   SECTION 10 — FINAL CTA
   =============================================================== */
function FinalCta() {
  const supportEmail = useSupportEmail();
  return (
    <section className="py-28 sm:py-36 bg-white" data-testid="section-final-cta">
      <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
        <motion.h2 {...reveal} className="font-serif-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight">
          Porta la tua attività bridal a un livello superiore
        </motion.h2>
        <motion.p {...reveal} className="mt-6 text-lg text-neutral-600 font-light leading-relaxed">
          Organizza ogni sposa con uno strumento creato appositamente per Bridal Hair Stylist e Makeup Artist.
        </motion.p>
        <motion.div {...reveal} className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link to="/register" data-testid="final-cta-primary">
            <Button size="lg" className="rounded-full px-9 h-14 text-base font-medium">
              Inizia gratis <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
            </Button>
          </Link>
          <a href={`mailto:${supportEmail}?subject=Richiesta%20demo%20AI%20Bridal%20Manager`} data-testid="final-cta-secondary">
            <Button variant="outline" size="lg" className="rounded-full px-7 h-14 text-base font-medium border-neutral-300 hover:bg-neutral-50">
              <Mail className="w-4 h-4 mr-2" strokeWidth={1.5} /> Richiedi una demo
            </Button>
          </a>
        </motion.div>
      </div>
    </section>
  );
}

/* ================================================================
   PAGE — sticky nav + sections + footer
   =============================================================== */
const NAV_LINKS = [
  { href: "#piattaforma",   label: "Piattaforma",   testid: "nav-piattaforma" },
  { href: "#moduli",        label: "Moduli",         testid: "nav-moduli" },
  { href: "#come-funziona", label: "Come funziona",  testid: "nav-come-funziona" },
  { href: "#prezzi",        label: "Prezzi",         testid: "nav-prezzi" },
];

export default function Landing() {
  const [menuOpen, setMenuOpen] = useState(false);
  const supportEmail = useSupportEmail();
  // Sticky nav effect — Apple-like: transparent over the hero, then
  // collapses into a denser opaque pill once the user scrolls past the
  // first viewport. Threshold = 80 px so it kicks in immediately after
  // the brand mark passes under the navbar. We listen with `passive:true`
  // to avoid scroll-jank on lower-end devices.
  const [scrolled, setScrolled] = useState(false);
  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      <header
        className={
          "fixed top-0 left-0 right-0 z-50 transition-all duration-300 " +
          (scrolled
            ? "bg-white/85 backdrop-blur-xl border-b border-neutral-200/70 shadow-[0_4px_20px_-12px_rgba(60,40,20,0.18)]"
            : "bg-transparent border-b border-transparent")
        }
        data-testid="landing-nav"
        data-scrolled={scrolled ? "true" : "false"}
      >
        <nav className={`max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between transition-all duration-300 ${scrolled ? "h-14" : "h-16 sm:h-20"}`}>
          <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
            <BrandLogo size={scrolled ? 26 : 30} />
            <span className={`font-serif-heading tracking-tight transition-all ${scrolled ? "text-base sm:text-lg" : "text-lg sm:text-xl"}`}>
              AI Bridal Manager<sup className="text-[10px] text-primary">™</sup>
            </span>
          </Link>
          <div className="hidden md:flex items-center gap-7">
            {NAV_LINKS.map((l) => (
              <a key={l.href} href={l.href} className="text-sm text-neutral-700 hover:text-primary transition-colors" data-testid={l.testid}>
                {l.label}
              </a>
            ))}
            <a
              href={`mailto:${supportEmail}?subject=Richiesta%20demo%20AI%20Bridal%20Manager`}
              className="text-sm text-neutral-700 hover:text-primary transition-colors"
              data-testid="nav-demo"
            >
              Demo
            </a>
            <Link to="/login" className="text-sm text-neutral-700 hover:text-primary transition-colors" data-testid="nav-login">
              Accedi
            </Link>
            <Link to="/register" data-testid="nav-register">
              <Button size="sm" className="rounded-full px-5 h-9 text-sm">Inizia gratis</Button>
            </Link>
          </div>
          <button
            className="md:hidden p-2"
            onClick={() => setMenuOpen(!menuOpen)}
            data-testid="nav-menu-toggle"
            aria-label="Menu"
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </nav>
        {menuOpen && (
          <div className="md:hidden border-t border-neutral-100 bg-white" data-testid="nav-mobile-menu">
            <div className="px-6 py-4 flex flex-col gap-3 text-sm">
              {NAV_LINKS.map((l) => (
                <a key={l.href} href={l.href} onClick={() => setMenuOpen(false)} className="py-1">
                  {l.label}
                </a>
              ))}
              <a
                href={`mailto:${supportEmail}?subject=Richiesta%20demo`}
                onClick={() => setMenuOpen(false)}
                className="py-1"
              >
                Demo
              </a>
              <Link to="/login" className="py-1" onClick={() => setMenuOpen(false)}>Accedi</Link>
              <Link to="/register" onClick={() => setMenuOpen(false)}>
                <Button size="sm" className="rounded-full w-full">Inizia gratis</Button>
              </Link>
            </div>
          </div>
        )}
      </header>

      <Hero />
      <PlatformSection />
      <ModulesSection />
      <HowItWorksSection />
      <TrialSection />
      <WeddingDaySection />
      <AIManagerSection />
      <BenefitsSection />
      <PricingSection />
      <FaqSection />
      <FinalCta />

      <Footer variant="default" />
    </div>
  );
}
